import type {
  PullRequestCheck,
  PullRequestCockpit,
  PullRequestIssue,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import {
  pullRequestIssues,
  pullRequestMergeDisabledReason,
  unresolvedPullRequestThreads,
  visiblePullRequestFeedback,
} from '@crowdex/workspace-protocol/pull-request-cockpit';

/** How one CI check reads in the panel (D36): passed, failed, still running, or neither. */
export type CheckTone = 'passed' | 'failed' | 'running' | 'neutral';

export function checkTone(check: PullRequestCheck): CheckTone {
  if (check.status !== 'completed' || check.conclusion === null) return 'running';
  if (
    check.conclusion === 'failure' ||
    check.conclusion === 'timed_out' ||
    check.conclusion === 'action_required'
  )
    return 'failed';
  if (check.conclusion === 'success') return 'passed';
  return 'neutral';
}

/** "38 s", "2 min", "1 h 5 min": the span between two instants, or '' when unknown. */
export function formatDuration(from: string | null, to: string | number | null): string {
  if (from === null || to === null) return '';
  const start = Date.parse(from);
  const end = typeof to === 'number' ? to : Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '';
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** The right-hand note of a check row: its duration, prefixed by its state when not passed. */
export function checkNote(check: PullRequestCheck, now: number = Date.now()): string {
  const tone = checkTone(check);
  if (tone === 'running') {
    const label = check.status === 'queued' ? 'queued' : 'running';
    const elapsed = formatDuration(check.startedAt, now);
    return elapsed ? `${label} · ${elapsed}` : label;
  }
  const duration = formatDuration(check.startedAt, check.completedAt);
  if (tone === 'passed') return duration;
  const label = tone === 'failed' ? 'failed' : (check.conclusion ?? 'skipped').replace('_', ' ');
  return duration ? `${label} · ${duration}` : label;
}

export type ChecksOverview = {
  passed: number;
  total: number;
  /** The checks that did not pass yet: failing first, then running. */
  attention: PullRequestCheck[];
  tone: 'failed' | 'running' | 'passed' | 'none';
  title: string;
};

export function checksOverview(cockpit: PullRequestCockpit): ChecksOverview {
  const tones = cockpit.checks.map((check) => ({ check, tone: checkTone(check) }));
  const passed = tones.filter((item) => item.tone === 'passed' || item.tone === 'neutral').length;
  const failed = tones.filter((item) => item.tone === 'failed').map((item) => item.check);
  const running = tones.filter((item) => item.tone === 'running').map((item) => item.check);
  const total = cockpit.checks.length;
  return {
    passed,
    total,
    attention: [...failed, ...running],
    tone:
      total === 0
        ? 'none'
        : failed.length > 0
          ? 'failed'
          : running.length > 0
            ? 'running'
            : 'passed',
    title: total === 0 ? 'Checks · none reported' : `Checks · ${passed} of ${total} passed`,
  };
}

export type ReviewOverview = {
  title: string;
  /** What the review waits on, or who decided. */
  detail: string;
  /** The approval waits on someone. */
  waiting: boolean;
  comments: number;
  unresolved: number;
};

function decidedBy(cockpit: PullRequestCockpit, state: 'approved' | 'changes_requested'): string[] {
  return [
    ...new Set(
      cockpit.feedback.filter((item) => item.state === state).map((item) => item.author.login),
    ),
  ];
}

export function reviewOverview(cockpit: PullRequestCockpit): ReviewOverview {
  const feedback = visiblePullRequestFeedback(cockpit);
  const comments = feedback.threads.length + feedback.standalone.length;
  const unresolved = unresolvedPullRequestThreads(cockpit).length;
  const base = { comments, unresolved };
  if (cockpit.reviewDecision === 'approved') {
    const by = decidedBy(cockpit, 'approved');
    return {
      ...base,
      title: 'Review · approved',
      detail: by.length > 0 ? `Approved by ${by.join(', ')}` : 'Approved',
      waiting: false,
    };
  }
  if (cockpit.reviewDecision === 'changes_requested') {
    const by = decidedBy(cockpit, 'changes_requested');
    return {
      ...base,
      title: 'Review · changes requested',
      detail: by.length > 0 ? `Changes requested by ${by.join(', ')}` : 'Changes requested',
      waiting: false,
    };
  }
  if (cockpit.reviewDecision === 'review_required') {
    const requested = cockpit.requestedReviewers.map((reviewer) => reviewer.login);
    return {
      ...base,
      title: 'Review · waiting',
      detail:
        requested.length > 0
          ? `Approval required · asked ${requested.join(', ')}`
          : 'Approval required',
      waiting: true,
    };
  }
  return { ...base, title: 'Review', detail: 'No approval required', waiting: false };
}

/** The one filled button of the panel, or none (merged, closed). */
export type PrimaryAction =
  | { kind: 'ready'; label: 'Ready for review' }
  | { kind: 'merge'; label: 'Merge' }
  | { kind: 'merge-when-ready'; label: 'Merge when ready'; disabledReason: string | null }
  | { kind: 'auto-merge-on'; label: 'Merging when ready' };

export function primaryAction(cockpit: PullRequestCockpit): PrimaryAction | null {
  const state = cockpit.pullRequest.state;
  if (state === 'merged' || state === 'closed') return null;
  if (state === 'draft') return { kind: 'ready', label: 'Ready for review' };
  if (cockpit.autoMergeEnabled) return { kind: 'auto-merge-on', label: 'Merging when ready' };
  if (pullRequestMergeDisabledReason(cockpit) === null) return { kind: 'merge', label: 'Merge' };
  return {
    kind: 'merge-when-ready',
    label: 'Merge when ready',
    disabledReason:
      cockpit.mergeable === 'conflicting' ? 'Resolve merge conflicts before merging.' : null,
  };
}

/** The one outline "fix" button beside it: what to hand the agent first, if anything. */
export function fixAction(
  cockpit: PullRequestCockpit,
): { label: string; issues: PullRequestIssue[] } | null {
  if (cockpit.pullRequest.state !== 'open' && cockpit.pullRequest.state !== 'draft') return null;
  const issues = pullRequestIssues(cockpit);
  const conflict = issues.filter((issue) => issue.type === 'merge_conflict');
  if (conflict.length > 0) return { label: 'Resolve conflicts', issues: conflict };
  const checks = issues.filter((issue) => issue.type === 'failing_check');
  if (checks.length > 0)
    return { label: checks.length === 1 ? 'Fix the check' : 'Fix the checks', issues: checks };
  const threads = issues.filter((issue) => issue.type === 'review_thread');
  if (threads.length > 0)
    return {
      label: threads.length === 1 ? 'Fix the comment' : 'Fix the comments',
      issues: threads,
    };
  return null;
}

export function stateLabel(state: PullRequestCockpit['pullRequest']['state']): string {
  if (state === 'draft') return 'Draft';
  if (state === 'merged') return 'Merged';
  if (state === 'closed') return 'Closed';
  return 'Open';
}
